import React from 'react';
import { motion } from 'framer-motion';
import { StarIcon, ShieldCheckIcon, ClockIcon, MapPinIcon, StethoscopeIcon, EyeIcon, ScanEyeIcon, CircleDotIcon, DropletIcon, BabyIcon, SparklesIcon } from 'lucide-react';
import { HeroSection } from '../components/HeroSection';
import { NearestLocationFinder } from '../components/NearestLocationFinder';
import { FAQSection } from '../components/FAQSection';
const BOOKING_URL = 'https://appointments.4patientcare.app/?CoverKey=5118&Source=website%20WebSchedV4&ReferredBy=Unknon&Gcount=0';
const services = [{
  title: 'Eye Exams',
  description: 'Comprehensive vision testing and eye health evaluations for all ages.',
  icon: EyeIcon
}, {
  title: 'Retinal Screening',
  description: 'Advanced imaging to detect early signs of eye diseases like glaucoma and macular degeneration.',
  icon: ScanEyeIcon
}, {
  title: 'Contact Lenses',
  description: 'Expert fittings for all types of contacts, including astigmatism and multifocal lenses.',
  icon: CircleDotIcon
}, {
  title: 'Dry Eye Therapy',
  description: 'Personalized treatment plans to relieve discomfort and restore tear film balance.',
  icon: DropletIcon
}, {
  title: 'Pediatric Eye Care',
  description: "Gentle, specialized vision care to ensure your child's eyes develop properly.",
  icon: BabyIcon
}, {
  title: 'LASIK Consultation',
  description: 'Pre-operative evaluations and post-operative care for laser vision correction.',
  icon: SparklesIcon
}];
export function LandingPage() {
  return <div className="min-h-screen bg-white" data-id="element-924">
      {/* Hero Section */}
      <HeroSection headline="See Clearly. Book in Minutes." subheadline="Comprehensive eye care for the whole family. Accepting new patients now. Same-week appointments available." data-id="element-925" />

      {/* Social Proof / Trust Bar */}
      <section className="bg-brand-gold-pale py-8 border-y border-brand-gold/20" data-id="element-926">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-927">
          <div className="flex flex-wrap justify-center md:justify-between items-center gap-8 text-brand-dark font-semibold" data-id="element-928">
            <div className="flex items-center gap-2" data-id="element-929">
              <div className="flex text-brand-gold" data-id="element-930">
                {[1, 2, 3, 4, 5].map(i => <StarIcon key={i} className="w-5 h-5 fill-current" data-id="element-931" />)}
              </div>
              <span data-id="element-932">4.9/5 Average Rating</span>
            </div>
            <div className="flex items-center gap-2" data-id="element-933">
              <StethoscopeIcon className="w-6 h-6 text-brand-gold" data-id="element-934" />
              <span data-id="element-935">Board Certified Doctors</span>
            </div>
            <div className="flex items-center gap-2" data-id="element-936">
              <MapPinIcon className="w-6 h-6 text-brand-gold" data-id="element-937" />
              <span data-id="element-938">6 Convenient MD Locations</span>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 md:py-28 bg-white" data-id="element-939">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-940">
          <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-941">
            <h2 className="text-3xl md:text-4xl font-heading font-bold text-brand-dark mb-6" data-id="element-942">
              Why Choose Visual Eyes Group?
            </h2>
            <p className="text-lg text-neutral-text/80" data-id="element-943">
              We combine advanced medical technology with a patient-first
              approach to ensure you get the best possible care for your eyes.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 mb-16" data-id="element-944">
            {[{
            title: 'Comprehensive Exams',
            desc: 'We go beyond just checking your prescription. We screen for early signs of eye diseases like glaucoma and cataracts.',
            icon: ShieldCheckIcon
          }, {
            title: 'Designer Frames',
            desc: 'Choose from hundreds of premium frames from top brands. Our stylists will help you find the perfect fit for your face.',
            icon: StarIcon
          }, {
            title: 'Convenient Scheduling',
            desc: 'With 6 locations across Maryland and easy online booking, getting quality eye care has never been simpler.',
            icon: ClockIcon
          }].map((benefit, i) => <div key={i} className="bg-surface-muted p-8 rounded-2xl border border-gray-100" data-id="element-945">
                <div className="w-14 h-14 bg-brand-gold/10 rounded-full flex items-center justify-center mb-6 text-brand-gold" data-id="element-946">
                  <benefit.icon className="w-7 h-7" data-id="element-947" />
                </div>
                <h3 className="text-xl font-bold text-brand-dark mb-3" data-id="element-948">
                  {benefit.title}
                </h3>
                <p className="text-neutral-text/80 leading-relaxed" data-id="element-949">
                  {benefit.desc}
                </p>
              </div>)}
          </div>

          <div className="text-center" data-id="element-950">
            <a href={BOOKING_URL} target="_blank" rel="noreferrer" className="inline-block bg-brand-dark hover:bg-brand-dark/90 text-white px-10 py-4 rounded-button font-heading font-bold text-lg transition-colors shadow-lg focus-ring" data-id="element-951">
              Schedule Your Visit Today
            </a>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-20 md:py-28 bg-surface-muted" data-id="element-952">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-953">
          <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-954">
            <motion.h2 initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} className="text-section-mobile md:text-section text-brand-dark mb-4" data-id="element-955">
              Our Services
            </motion.h2>
            <motion.p initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: 0.1
          }} className="text-subtitle text-neutral-text/80" data-id="element-956">
              Comprehensive eye care tailored to your specific needs, using the
              latest technology.
            </motion.p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12" data-id="element-957">
            {services.map((service, index) => {
            const Icon = service.icon;
            return <motion.div key={service.title} initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              delay: index * 0.1
            }} className="bg-white p-8 rounded-card shadow-card border border-gray-100" data-id="element-958">
                  <div className="w-14 h-14 bg-brand-gold-pale rounded-full flex items-center justify-center mb-6" data-id="element-959">
                    <Icon className="w-7 h-7 text-brand-gold" data-id="element-960" />
                  </div>
                  <h3 className="text-xl font-heading font-semibold mb-3 text-brand-dark" data-id="element-961">
                    {service.title}
                  </h3>
                  <p className="text-neutral-text/70 leading-relaxed" data-id="element-962">
                    {service.description}
                  </p>
                </motion.div>;
          })}
          </div>

          <div className="text-center" data-id="element-963">
            <a href={BOOKING_URL} target="_blank" rel="noreferrer" className="inline-block bg-brand-gold hover:bg-brand-gold-light text-white px-10 py-4 rounded-button font-heading font-bold text-lg transition-colors shadow-lg focus-ring" data-id="element-964">
              Book an Appointment
            </a>
          </div>
        </div>
      </section>

      {/* Testimonial Section */}
      <section className="py-20 bg-brand-dark text-white relative overflow-hidden" data-id="element-965">
        <div className="absolute top-0 right-0 -mt-20 -mr-20 w-96 h-96 bg-brand-gold/10 rounded-full blur-3xl" data-id="element-966"></div>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10" data-id="element-967">
          <div className="flex justify-center mb-8 text-brand-gold" data-id="element-968">
            {[1, 2, 3, 4, 5].map(i => <StarIcon key={i} className="w-8 h-8 fill-current" data-id="element-969" />)}
          </div>
          <blockquote className="text-2xl md:text-4xl font-heading font-medium leading-tight mb-8" data-id="element-970">
            "The most thorough eye exam I've ever had. The staff was incredibly
            welcoming, and Dr. Nnabue took the time to explain everything
            clearly. I found the perfect frames too!"
          </blockquote>
          <p className="text-brand-gold font-semibold text-lg" data-id="element-971">
            — Sarah M., Patient since 2023
          </p>
        </div>
      </section>

      {/* FAQ Section */}
      <FAQSection data-id="element-972" />

      {/* Nearest Location Finder */}
      <NearestLocationFinder data-id="element-973" />

      {/* Final CTA */}
      <section className="py-24 bg-brand-gold-pale relative overflow-hidden" data-id="element-974">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10" data-id="element-975">
          <h2 className="text-4xl md:text-5xl font-heading font-bold text-brand-dark mb-6" data-id="element-976">
            Ready for Better Vision?
          </h2>
          <p className="text-xl text-brand-dark/80 mb-10 max-w-2xl mx-auto" data-id="element-977">
            Join thousands of satisfied patients who trust Visual Eyes Group
            with their eye health. Appointments fill up fast!
          </p>
          <a href={BOOKING_URL} target="_blank" rel="noreferrer" className="inline-block bg-brand-gold hover:bg-brand-gold-light text-white px-12 py-5 rounded-button font-heading font-bold text-xl transition-transform hover:scale-105 shadow-xl focus-ring" data-id="element-978">
            Book Your Appointment Now
          </a>
          <p className="mt-6 text-brand-dark/60 text-sm font-medium" data-id="element-979">
            Takes less than 2 minutes. No credit card required to book.
          </p>
        </div>
      </section>
    </div>;
}